import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { filterQuery, NotificationTab, RouteBody, RouteResponse, SearchKind } from '@chapsi/contracts';
import { ApiError, api } from './client.js';

/** Query keys in one place so realtime events can invalidate precisely. */
export const keys = {
  session: ['session'] as const,
  me: ['me'] as const,
  sidebar: ['sidebar'] as const,
  inbox: ['inbox'] as const,
  inboxList: (filter: 'all' | 'unread', tab: NotificationTab = 'all') => ['inbox', filter, tab] as const,
  reminders: (state: 'upcoming' | 'delivered') => ['reminders', state] as const,
  channel: (id: string) => ['channel', id] as const,
  messages: (id: string, projectId?: string | null) => ['messages', id, projectId ?? null] as const,
  /** A chat reply conversation, opened from any of its messages. Under the channel so a channel's events refresh it. */
  chatConversation: (channelId: string, messageId: string) => ['chatConversation', channelId, messageId] as const,
  glance: (id: string) => ['glance', id] as const,
  workItems: (q: Record<string, string | undefined>) => ['workItems', q] as const,
  workItem: (id: string) => ['workItem', id] as const,
  /** Under the work item's key, so anything that refreshes the item refreshes its conversation too. */
  conversation: (id: string) => ['workItem', id, 'conversation'] as const,
  supportInbox: ['supportInbox'] as const,
  statuses: ['statuses'] as const,
  /** People's status ("In a meeting until 3:00"), not work item statuses. */
  peopleStatuses: ['peopleStatuses'] as const,
  /** Who has a profile photo (docs/adr/0075). */
  peopleAvatars: ['peopleAvatars'] as const,
  myWork: ['myWork'] as const,
  myTimer: ['myTimer'] as const,
  /** My work across all workspaces (docs/adr/0061). Never persisted to the local cache (queryClient PERSISTED). */
  myWorkAllWorkspaces: ['myWorkAllWorkspaces'] as const,
  projects: (filter: string) => ['projects', filter] as const,
  project: (id: string) => ['project', id] as const,
  clients: ['clients'] as const,
  /** The Clients section (docs/adr/0078); one client's page sits under it, so refreshing the list refreshes the page. */
  clientsOverview: ['clients', 'overview'] as const,
  clientOverview: (id: string) => ['clients', 'overview', id] as const,
  projectMembers: (id: string) => ['project', id, 'members'] as const,
  projectFiles: (id: string) => ['project', id, 'files'] as const,
  members: ['members'] as const,
  adminMembers: ['members', 'admin'] as const,
  /** Teammates whose birthday it is today (docs/adr/0047). */
  birthdays: ['birthdays'] as const,
  features: ['features'] as const,
  settings: (scope: string, id?: string) => ['settings', scope, id ?? null] as const,
  audit: ['audit'] as const,
  shareLinks: ['shareLinks'] as const,
  devices: ['devices'] as const,
  /** The signed-in person's passkeys (docs/adr/0026-passkeys.md). */
  passkeys: ['passkeys'] as const,
  time: (q: Record<string, string | undefined>) => ['time', q] as const,
  /** Every search starts with ['search', q]: exact and similar-meaning lists for one query share the prefix. */
  search: (q: string, kind: SearchKind, limit: number, filters: SearchFilterQuery = {}, meaning = false) =>
    ['search', q, kind, limit, filters.from ?? '', filters.in ?? '', filters.after ?? '', filters.before ?? '', meaning ? 'meaning' : 'exact'] as const,
  /** Slack imports (docs/adr/0017-slack-import.md); one import's detail sits under the list. */
  imports: ['imports'] as const,
  importDetail: (id: string) => ['imports', id] as const,
  /** Imports from other apps (docs/adr/0043-import-from-other-apps.md). */
  workImports: ['work-imports'] as const,
  workImportDetail: (id: string) => ['work-imports', id] as const,
  /** Chapi (docs/adr/0021-chapi.md). One conversation sits under the list key's prefix. */
  chapiStatus: ['chapi', 'status'] as const,
  chapiConversations: ['chapi', 'conversations'] as const,
  chapiConversation: (id: string) => ['chapi', 'conversation', id] as const,
  chapiUsage: ['chapi', 'usage'] as const,
  chapiMorning: ['chapi', 'morning'] as const,
  /** Chapi and the work (docs/adr/0063): a breakdown, the person's "looks done" suggestions, client updates. */
  chapiBreakdown: (id: string) => ['chapi', 'breakdown', id] as const,
  chapiBriefFiles: (projectId: string) => ['chapi', 'brief-files', projectId] as const,
  chapiDoneSuggestions: ['chapi', 'done-suggestions'] as const,
  chapiClientUpdates: (projectId: string) => ['chapi', 'client-updates', projectId] as const,
  chapiPendingUpdates: ['chapi', 'client-updates'] as const,
  /** My day (docs/adr/0029-my-day.md): the person's own list. */
  myDay: ['myDay'] as const,
  /** Desktop sticky notes (docs/adr/0105): only fetched inside the desktop app. */
  myDayNotes: ['myDay', 'notes'] as const,
  /** Live catch-ups in channels the person reads (docs/adr/0057). */
  catchUps: ['catchUps'] as const,
};

export const useSession = () => useQuery({ queryKey: keys.session, queryFn: () => api('session'), retry: false, staleTime: 60_000 });
/** A workspace pending deletion is always re-checked: an owner may have restored it in another tab. */
/**
 * The signed-in person and workspace. `revalidate` (the app root only, once per page load) always asks the server on
 * mount: the copy restored from the device cache can be seconds old yet out of date (a feature just switched on).
 */
export const useMe = (opts: { revalidate?: boolean } = {}) =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api('me'),
    staleTime: (q) => (q.state.data?.workspace.status === 'pending_deletion' ? 0 : 60_000),
    ...(opts.revalidate ? { refetchOnMount: 'always' as const } : {}),
  });
export const useSidebar = () => useQuery({ queryKey: keys.sidebar, queryFn: () => api('sidebar') });
/** A page of the Notifications centre: unread or everything, on one tab (SC-20). */
export function useInbox(filter: 'all' | 'unread', tab: NotificationTab = 'all', enabled = true) {
  return useInfiniteQuery({
    queryKey: keys.inboxList(filter, tab),
    queryFn: ({ pageParam }) => api('inbox', { query: { filter, tab, before: pageParam ?? undefined, limit: 30 } }),
    enabled,
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}
export const useReminders = (state: 'upcoming' | 'delivered', enabled = true) =>
  useQuery({ queryKey: keys.reminders(state), queryFn: () => api('listReminders', { query: { state } }), enabled });
export const useChannel = (id: string, enabled = true) => useQuery({ queryKey: keys.channel(id), queryFn: () => api('getChannel', { params: { channelId: id } }), enabled });
export const useGlance = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.glance(id), queryFn: () => api('glance', { params: { channelId: id } }), enabled });
export const useStatuses = (enabled = true) => useQuery({ queryKey: keys.statuses, queryFn: () => api('listStatuses'), staleTime: 5 * 60_000, enabled });
export const useMyWork = () => useQuery({ queryKey: keys.myWork, queryFn: () => api('myWork') });

/** My work across all workspaces (docs/adr/0061): a page of workspaces at a time, only while the tab is open. */
export const useMyWorkAllWorkspaces = (enabled: boolean) =>
  useInfiniteQuery({
    queryKey: keys.myWorkAllWorkspaces,
    queryFn: ({ pageParam }) => api('myWorkAllWorkspaces', { query: { cursor: pageParam } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: 30_000,
  });
export const useProjectsIndex = (filter: 'all' | 'mine' | 'archived') =>
  useQuery({ queryKey: keys.projects(filter), queryFn: () => api('projectsIndex', { query: { filter } }) });
export const useClients = (enabled = true) => useQuery({ queryKey: keys.clients, queryFn: () => api('listClients'), enabled });
/** One project (docs/adr/0078's project page): 404 for a project the person is not on (docs/adr/0074). */
export const useProject = (id: string) => useQuery({ queryKey: keys.project(id), queryFn: () => api('getProject', { params: { projectId: id } }), retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2 });
export const useClientsOverview = () => useQuery({ queryKey: keys.clientsOverview, queryFn: () => api('clientsOverview') });
export const useClientOverview = (id: string) =>
  useQuery({ queryKey: keys.clientOverview(id), queryFn: () => api('getClientOverview', { params: { clientId: id } }), retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2 });
export const useMembers = (enabled = true) => useQuery({ queryKey: keys.members, queryFn: () => api('listMembers', { query: { view: 'team' } }), staleTime: 60_000, enabled });
/** Workspace settings > Members (docs/adr/0047): owners and admins see every email and phone. Under keys.members, so it refreshes with it. */
export const useAdminMembers = () => useQuery({ queryKey: keys.adminMembers, queryFn: () => api('listMembers', { query: { view: 'admin' } }), staleTime: 60_000 });
export const useWorkItems = (q: Record<string, string | undefined>, enabled = true) =>
  useQuery({ queryKey: keys.workItems(q), queryFn: () => api('listWorkItems', { query: q }), enabled });
/** The filter fields of a search request (from, in, after, before). */
export type SearchFilterQuery = ReturnType<typeof filterQuery>;

export interface SearchOptions {
  q: string;
  kind: SearchKind;
  limit: number;
  filters?: SearchFilterQuery;
  /** Also ask for "similar meaning" results (docs/adr/0036-search-by-meaning.md). Slower, so only when asked. */
  meaning?: boolean;
  enabled?: boolean;
}

/**
 * Workspace search (docs/adr/0012-search.md, 0036). Runs only for 2+ characters; keeps the previous results
 * on screen while the next query loads so lists do not collapse between keystrokes. Ask for exact results
 * and similar-meaning results as two queries, so exact ones never wait for the slower list.
 */
export const useSearchResults = ({ q, kind, limit, filters = {}, meaning = false, enabled = true }: SearchOptions) =>
  useQuery({
    queryKey: keys.search(q.trim(), kind, limit, filters, meaning),
    queryFn: ({ signal }) => api('search', { query: { q: q.trim(), kind, limit, ...filters, meaning: meaning ? '1' : '0' }, signal }),
    enabled: enabled && q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 1,
  });
export const useWorkItem = (id: string | null | undefined) =>
  useQuery({ queryKey: keys.workItem(id ?? ''), queryFn: () => api('getWorkItem', { params: { itemId: id! } }), enabled: !!id });
/** A support ticket's emails in and out, with its first-response and resolution clocks (docs/adr/0013-support-desk.md). */
export const useTicketConversation = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.conversation(id), queryFn: () => api('ticketConversation', { params: { itemId: id } }), enabled });
export const useSupportInbox = (enabled = true) => useQuery({ queryKey: keys.supportInbox, queryFn: () => api('getSupportInbox'), enabled });
export const useSettings = (scope: 'workspace' | 'person' | 'project', projectId?: string) =>
  useQuery({
    queryKey: keys.settings(scope, projectId),
    queryFn: () =>
      scope === 'workspace'
        ? api('getWorkspaceSettings')
        : scope === 'person'
          ? api('getMySettings')
          : api('getProjectSettings', { params: { projectId: projectId! } }),
    staleTime: 60_000,
  });

export function useMessages(channelId: string, projectId?: string | null) {
  return useInfiniteQuery({
    queryKey: keys.messages(channelId, projectId),
    queryFn: ({ pageParam }) =>
      api('listMessages', { params: { channelId }, query: { before: pageParam ?? undefined, projectId: projectId ?? undefined, limit: 50 } }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextBefore,
  });
}

/** A message's reply conversation: the first message, every reply in order, and whether the reader muted it. */
export function useChatConversation(channelId: string, messageId: string | null) {
  return useQuery({
    queryKey: keys.chatConversation(channelId, messageId ?? ''),
    queryFn: () => api('getConversation', { params: { messageId: messageId! } }),
    enabled: !!messageId,
  });
}

/** A mutation that refreshes the given keys afterwards. */
export function useApiMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>, invalidate: (args: TArgs, result: TResult) => QueryKey[] = () => []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result, args) => {
      for (const key of invalidate(args, result)) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export type MessagesData = { pages: RouteResponse<'listMessages'>[]; pageParams: (number | null)[] };
export type { RouteBody };
